import React, { useEffect, Suspense, lazy } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "utils/AuthContext";
import Verification from "views/authentications/Verification";
import AssignmentRoutes from "views/students/Assignments/AssignmentRoutes";
import TeacherAssignmentRoutes from "views/Teacher/Assignments/AssignmentRoutes";
import TeacherClassesAndGroupsRoutes from "views/Teacher/ClassesAndGroups/ClassesAndGroupsRoutes";
import Loader from "components/layouts/Loader";
import VocabularyRoutesTeacher from "views/Teacher/Vocabulary/VocabularyRoutes";
import ContactRoutes from "views/Teacher/Contact/ContactRoutes";
import ComingSoon from "views/shared/ComingSoon";
import AttemptExercise from "views/students/Assignments/AttemptExercise";
import SafeLogin from "views/authentications/SafeLogin";
import AttemptSafeExercise from "views/students/Assignments/AttemptSafeExercise";
import Thanks from "components/layouts/Thanks";
import ErrorSafe from "components/layouts/ErrorSafe";
import PackagesRoutes from "views/Teacher/Packages/PackagesRoutes";
const Quiz = lazy(() => import("views/students/Vocabulary/Quiz"));
const LessonPlanner = lazy(() =>
  import("views/Teacher/LessonPlanner/LessonPlanner")
);
const HelpCenterRoutes = lazy(() =>
  import("views/Admin/help-center/HelpCenterRoutes")
);
const ExerciseManagementRoutes = lazy(() =>
  import("views/Admin/exercise-management/ExerciseManagementRoutes")
);
const LibraryManagementRoutes = lazy(() =>
  import("views/Admin/library-management/LibraryManagementRoutes")
);
const ReportedContentRoutes = lazy(() =>
  import("views/Admin/reported-content/ReportedContentRoutes")
);
const Settings = lazy(() => import("views/Admin/settings/Settings"));
const UserManagementRoutes = lazy(() =>
  import("views/Admin/user-management/UserManagementRoutes")
);
const ForgetPassword = lazy(() =>
  import("views/authentications/ForgetPassword")
);
const Login = lazy(() => import("views/authentications/Login"));
const OTP = lazy(() => import("views/authentications/Otp"));
const Dashboard = lazy(() => import("views/Teacher/Dashboard/Dashboard"));
const Contact = lazy(() => import("views/Teacher/Contact/Contact"));
const StatisticMain = lazy(() =>
  import("views/Teacher/Statistics/StatisticMain")
);
const UsersRoutes = lazy(() => import("views/Teacher/users/UserRoutes"));
const ExercisesRoutes = lazy(() =>
  import("views/Teacher/Exercises/ExercisesRoutes")
);

const Layout = lazy(() => import("components/layouts/Layout"));
const Results = lazy(() => import("views/students/Results/Results"));
const ClassesAndGroupsRoutes = lazy(() =>
  import("views/students/classesandgroups/ClassesAndGroupsRoutes")
);
const StudentLibrary = lazy(() => import("views/students/Library/Library"));
const ExtraExercises = lazy(() =>
  import("views/students/Exercises/ExtraExercises")
);
const VocabularyRoutes = lazy(() =>
  import("views/students/Vocabulary/VocabularyRoutes")
);
const Notifications = lazy(() =>
  import("views/students/Notifications/Notifications")
);
const StudentDashboard = lazy(() =>
  import("views/students/Dashboard/Dashboard")
);
const PresentationLibrary = lazy(() =>
  import("views/Admin/PresentationLibrary/PresentationLibrary")
);
const SignIn = lazy(() => import("views/authentications/SignIn"));
const MessageManagement = lazy(() =>
  import("views/Admin/message-management/MessageManagement")
);
const NotFound = lazy(() => import("components/layouts/NoFound"));

const Navigation = () => {
  const { isVerified, loading, userRoles, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // "/" is now the Home route itself, so it only needs redirecting when the
    // user is unverified or is a superUser (whose home is /user-management).
    const needsRedirect =
      location.pathname === "/" &&
      (!isVerified || userRoles === "superUser" || !user?.school ||
        (user?.role === "user" && !user?.class));

    if ((needsRedirect && !loading) ||
      (location.pathname === "/dashboard" && !loading)
    ) {
      if (isVerified) {
        if (
          (!user?.school || (user?.role === "user" && !user?.class)) &&
          user.role !== "superUser"
        ) {
          navigate("/verification"); // Navigate to verification route
          return;
        }

        switch (userRoles) {
          case "superUser":
            navigate("/user-management");
            break;
          case "teacher":
            navigate("/");
            break;
          case "admin":
            navigate("/");
            break;
          case "user":
            navigate("/");
            break;
          default:
            navigate("/login");
        }
      } else {
        navigate("/login");
      }
    }
  }, [isVerified, navigate, location.pathname, loading, userRoles]);

  if (loading) {
    return <Loader />;
  }

  return (
    <Suspense fallback={<Loader />}>
      <Routes>
        {isVerified &&
          (userRoles === "superUser" ||
            (user?.school && (user?.role !== "user" || user?.class))) ? (
          <Route element={<Layout />}>
            {userRoles === "superUser" && (
              <>
                <Route
                  path="/user-management/*"
                  element={<UserManagementRoutes />}
                />
                <Route path="/exercise/*" element={<ExercisesRoutes />} />
                <Route path="/message-management" element={<ComingSoon />} />
                <Route
                  path="/exercise-management/*"
                  element={<ExerciseManagementRoutes />}
                />
                <Route
                  path="/vocabulary/*"
                  element={<VocabularyRoutesTeacher />}
                />
                <Route
                  path="/library-management/*"
                  element={<LibraryManagementRoutes />}
                />
                <Route
                  path="/reported-content/*"
                  element={<ReportedContentRoutes />}
                />
                <Route path="/settings" element={<Settings />} />
                <Route path="/help-center/*" element={<HelpCenterRoutes />} />
                <Route
                  path="/classes&groups/*"
                  element={<TeacherClassesAndGroupsRoutes />}
                />
                <Route
                  path="/classes/*"
                  element={<TeacherClassesAndGroupsRoutes view="classes" />}
                />
                <Route
                  path="/groups/*"
                  element={<TeacherClassesAndGroupsRoutes view="groups" />}
                />
                <Route
                  path="/packages/*"
                  element={<PackagesRoutes />}
                />

              </>
            )}
            {(userRoles === "teacher" || userRoles === "admin") && (
              <>
                <Route path="/" element={<Dashboard />} />
                <Route path="/dashboard" element={<Navigate to="/" replace />} />
                <Route path="/exercise/*" element={<ExercisesRoutes />} />
                <Route path="/statistics" element={<StatisticMain />} />
                <Route path="/message-management" element={<ComingSoon />} />
                <Route path="/contact-us/*" element={<ContactRoutes />} />
                <Route path="/users/*" element={<UsersRoutes />} />
                <Route
                  path="/classes&groups/*"
                  element={<TeacherClassesAndGroupsRoutes />}
                />
                <Route
                  path="/classes/*"
                  element={<TeacherClassesAndGroupsRoutes view="classes" />}
                />
                <Route
                  path="/groups/*"
                  element={<TeacherClassesAndGroupsRoutes view="groups" />}
                />
                <Route
                  path="/vocabulary/*"
                  element={<VocabularyRoutesTeacher />}
                />
                <Route
                  path="/assignments/*"
                  element={<TeacherAssignmentRoutes />}
                />
                {/* Submissions were merged into the Assignments page. */}
                <Route
                  path="/submissions/*"
                  element={<Navigate to="/assignments" replace />}
                />
                <Route path="/lesson-planner" element={<LessonPlanner />} />
                <Route path="/settings" element={<Settings />} />
              </>
            )}

            {(userRoles === "teacher" || userRoles === "superUser") && (
              <>
                <Route path="/presentation-library" element={<PresentationLibrary />} />
              </>
            )}

            {userRoles === "user" && (
              <>
                <Route path="/" element={<StudentDashboard />} />
                <Route path="/dashboard" element={<Navigate to="/" replace />} />
                {/* <Route path='/notifications' element={<Notifications />} /> */}
                <Route path="/vocabulary/*" element={<VocabularyRoutes />} />
                {/* <Route path='/vocabulary' element={<ComingSoon />} /> */}
                <Route path="/message-management" element={<ComingSoon />} />
                <Route path="/extra-exercises" element={<ExtraExercises />} />
                <Route path="/student-library" element={<ComingSoon />} />
                <Route path="/assignments/*" element={<AssignmentRoutes />} />
                <Route
                  path="/classes&groups/*"
                  element={<ClassesAndGroupsRoutes />}
                />
                <Route
                  path="/classes/*"
                  element={<ClassesAndGroupsRoutes view="classes" />}
                />
                <Route
                  path="/groups/*"
                  element={<ClassesAndGroupsRoutes view="groups" />}
                />
                <Route path="/lesson-planner" element={<LessonPlanner />} />
                <Route path="/statistics" element={<StatisticMain />} />
                <Route path="/results" element={<Results />} />
                <Route path="/settings" element={<Settings />} />
                {/* <Route path='/contact-us' element={<Contact />} /> */}
              </>
            )}
          </Route>
        ) : (
          isVerified && (
            <Route path="/verification" element={<Verification />} />
          )
        )}
        {isVerified && (
          <>
            <Route
              path="/quiz/attempt/:_id"
              element={<AttemptExercise padding={true} />}
            />
            <Route
              path="/Safequiz/attempt/:_id"
              element={<AttemptSafeExercise padding={true} />}
            />
            <Route path="/quiz/vocabulary/attempt/:_id" element={<Quiz />} />
          </>
        )}

        <Route path="/safe/login" element={<SafeLogin />} />
        {!isVerified && (
          <>
            <Route path="/login" element={<Login />} />
            <Route path="/forget-password" element={<ForgetPassword />} />
            <Route path="/otp" element={<OTP />} />
            <Route path="/signup" element={<SignIn />} />
          </>
        )}
        <Route path="/thanks" element={<Thanks />} />
        <Route path="/not-exist" element={<ErrorSafe />} />
        {!loading && <Route path="*" element={<NotFound />} />}
      </Routes>
    </Suspense>
  );
};

export default Navigation;
